import { DialogTitle } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { useFetchAssortmentTags } from '@retail/my-assortment/data-access';
import { AssortmentItemMinimal, AssortmentTag } from '@retail/my-assortment/types';
import { useFilters } from '@retail/products/hooks';
import { BulkActionsFilters, itemBulkActions } from '@retail/products/utils';
import { Dialog, Stepper } from '@shared/components';
import { useCallback, useMemo, useState } from 'react';
import { HandleItems, ItemBatch } from './HandleItems';
import { HandleItemTags } from './HandleItemTags';
import { useAddItemsToAssortment } from './useAddItemsToAssortment';

const emptyBatch: ItemBatch = { items: [], rest: [] };

interface Props {
  open: boolean;
  assortmentId: number;
  selectedItems: AssortmentItemMinimal[];
  onClose: () => void;
}

function AddCheckedItemsToAssortment({ open, assortmentId, selectedItems, onClose }: Props) {
  const t = useAppTFunction();

  const { resetFilters } = useFilters<BulkActionsFilters>(itemBulkActions);

  const handleOnClose = useCallback(() => {
    resetFilters();
    onClose();
  }, [onClose, resetFilters]);

  const { data: assortmentTagDtos = [] } = useFetchAssortmentTags({
    assortmentIds: [assortmentId],
  });
  const assortmentTags = useMemo(
    () => assortmentTagDtos.map(AssortmentTag.fromDTO),
    [assortmentTagDtos]
  );
  const includeTagStep = assortmentTags.length > 0;

  const steps = useMemo(
    () => [t('myAssortment.actions.editItems'), t('myAssortment.actions.editTags')],
    [t]
  );

  const itemsWithMgItemNumber = useMemo(
    () => selectedItems.filter((item) => !!item.mgItemNumber),
    [selectedItems]
  );

  const [activeStep, setActiveStep] = useState(0);
  const [items, setItems] = useState<AssortmentItemMinimal[]>(itemsWithMgItemNumber);
  const [batchToTag, setBatchToTag] = useState<ItemBatch>(emptyBatch);

  const continueWithRemainingItems = useCallback(
    (rest: AssortmentItemMinimal[]) => {
      resetFilters();
      setItems(rest);
      setActiveStep(0);
    },
    [resetFilters]
  );

  const { addItems, isSaving, resetItemsPersisted } = useAddItemsToAssortment({
    onAllItemsAdded: handleOnClose,
    onItemsRemaining: continueWithRemainingItems,
  });

  const handleItemsEdited = useCallback(
    (batch: ItemBatch) => {
      setItems([...batch.items, ...batch.rest]);
      resetItemsPersisted();

      if (!includeTagStep) {
        addItems(batch);
        return;
      }
      setBatchToTag(batch);
      setActiveStep(1);
    },
    [addItems, includeTagStep, resetItemsPersisted]
  );

  return (
    <Dialog open={open} onClose={handleOnClose} fullScreen sx={{ p: 4 }}>
      <DialogTitle sx={{ justifyContent: 'space-between' }}>
        {t('products.mgItem.addItemsToAssortment.handleSelectedItems')}
        {includeTagStep && (
          <Stepper steps={steps} activeStep={activeStep} labelOrientation="horizontal" />
        )}
      </DialogTitle>

      {activeStep === 0 && (
        <HandleItems
          items={items}
          setItems={setItems}
          itemsWithoutItemNr={selectedItems.length - itemsWithMgItemNumber.length}
          continueToTags={includeTagStep}
          continueToCustomerAssortments={false}
          isSaving={isSaving}
          onContinue={handleItemsEdited}
          onClose={handleOnClose}
        />
      )}
      {activeStep === 1 && (
        <HandleItemTags
          assortmentItems={batchToTag.items}
          assortmentTags={assortmentTags}
          isSaving={isSaving}
          continueToCustomerAssortments={false}
          onAddItems={(taggedItems, tagUpdates) =>
            addItems({ items: taggedItems, rest: batchToTag.rest }, tagUpdates)
          }
          onClose={handleOnClose}
          goBack={() => setActiveStep(0)}
        />
      )}
    </Dialog>
  );
}

export default withDialogSuspenseErrorBoundary(AddCheckedItemsToAssortment);
